import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DataTable, { formatUGX } from "components/DataTable/DataTable.jsx";

import api from "api/index";

const styles = {
  reportCard: { cursor: "pointer", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "16px 20px", marginBottom: "12px", transition: "box-shadow 0.2s" },
  reportTitle: { fontWeight: "bold", color: "#1a237e", fontSize: "14px", margin: 0 },
  reportDesc: { color: "#666", fontSize: "12px", margin: "4px 0 0" },
  formControl: { width: "100%", marginTop: "8px" },
  errorMsg: { color: "#f44336", fontSize: "13px" }
};

const REPORTS = [
  { key: "enrolment_summary", title: "Enrolment Summary", desc: "Total students by programme, year, and gender" },
  { key: "fee_collection", title: "Fee Collection Report", desc: "Revenue collected vs outstanding per semester" },
  { key: "exam_results", title: "Exam Results Summary", desc: "Pass/fail rates per course and semester" },
  { key: "staff_summary", title: "Staff Summary", desc: "Staff by department, contract type, and gender" },
  { key: "nche_return", title: "NCHE Statistical Return", desc: "Annual statistical return for NCHE compliance" },
  { key: "clearance_status", title: "Clearance Status Report", desc: "Students awaiting or completed clearance" }
];

class Reports extends Component {
  state = {
    activeReport: null,
    semesters: [],
    selectedSemester: "",
    reportData: null,
    loading: false,
    error: null
  };

  componentDidMount() { this.fetchSemesters(); }

  fetchSemesters = async () => {
    try {
      const res = await api.get("/semesters?limit=100");
      const d = res.data.data || res.data;
      const list = Array.isArray(d) ? d : d.data || [];
      const current = list.find(s => s.isCurrent);
      this.setState({ semesters: list, selectedSemester: current ? current.id : "" });
    } catch (e) { /* silent */ }
  };

  runReport = async () => {
    const { activeReport, selectedSemester } = this.state;
    if (!activeReport) return;
    this.setState({ loading: true, error: null, reportData: null });
    try {
      const params = {};
      if (selectedSemester) params.semesterId = selectedSemester;
      const res = await api.get(`/reports/${activeReport.key}`, { params });
      this.setState({ reportData: res.data.data || res.data, loading: false });
    } catch (e) {
      this.setState({ error: "Failed to generate report.", loading: false });
    }
  };

  renderTable = () => {
    const { reportData, activeReport } = this.state;
    if (!reportData) return null;

    if (Array.isArray(reportData.rows) && reportData.headers) {
      const headers = reportData.headers;
      const columns = headers.map((h, idx) => ({
        id: `col-${idx}`,
        header: h,
        accessorFn: r => (r[h] != null ? r[h] : "—")
      }));
      return <DataTable columns={columns} data={reportData.rows} />;
    }

    if (activeReport?.key === "enrolment_summary") {
      const items = Array.isArray(reportData) ? reportData : [];
      const columns = [
        { id: "programme", header: "Programme", accessorFn: r => r.programme || "—" },
        { id: "year1", header: "Year 1", accessorFn: r => r.year1 || 0 },
        { id: "year2", header: "Year 2", accessorFn: r => r.year2 || 0 },
        { id: "year3", header: "Year 3", accessorFn: r => r.year3 || 0 },
        { id: "total", header: "Total", accessorFn: r => r.total || 0 },
        { id: "male", header: "Male", accessorFn: r => r.male || 0 },
        { id: "female", header: "Female", accessorFn: r => r.female || 0 }
      ];
      return <DataTable columns={columns} data={items} emptyState="No data for selected filters." />;
    }

    if (activeReport?.key === "fee_collection") {
      const items = Array.isArray(reportData) ? reportData : [];
      const columns = [
        { id: "programme", header: "Programme", accessorFn: r => r.programme || "—" },
        {
          id: "invoiced",
          header: "Invoiced (UGX)",
          accessorFn: r => r.invoiced || 0,
          cell: info => formatUGX(info.getValue()),
          meta: { align: "right" }
        },
        {
          id: "collected",
          header: "Collected (UGX)",
          accessorFn: r => r.collected || 0,
          cell: info => formatUGX(info.getValue()),
          meta: { align: "right" }
        },
        {
          id: "outstanding",
          header: "Outstanding (UGX)",
          accessorFn: r => r.outstanding || 0,
          cell: info => formatUGX(info.getValue()),
          meta: { align: "right" }
        },
        {
          id: "pctCollected",
          header: "% Collected",
          accessorFn: r => (r.invoiced > 0 ? Math.round((r.collected / r.invoiced) * 100) : null),
          cell: info => (info.getValue() != null ? `${info.getValue()}%` : "—"),
          meta: { align: "right" }
        }
      ];
      return <DataTable columns={columns} data={items} emptyState="No data for selected filters." />;
    }

    return (
      <pre style={{ fontSize: "12px", color: "#333", background: "#f5f5f5", padding: "12px", borderRadius: "6px", overflowX: "auto" }}>
        {JSON.stringify(reportData, null, 2)}
      </pre>
    );
  };

  render() {
    const { classes } = this.props;
    const { activeReport, semesters, selectedSemester, loading, error, reportData } = this.state;

    return (
      <GridContainer>
        <GridItem xs={12} sm={4}>
          <Card>
            <CardHeader>
              <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Reports</h4>
              <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                NCHE Compliance &amp; Analytics
              </p>
            </CardHeader>
            <CardBody style={{ padding: "12px 16px" }}>
              {REPORTS.map(r => (
                <div
                  key={r.key}
                  className={classes.reportCard}
                  onClick={() => this.setState({ activeReport: r, reportData: null, error: null })}
                  style={{ background: activeReport?.key === r.key ? "#e8eaf6" : "#fff", borderColor: activeReport?.key === r.key ? "#3949ab" : "#e0e0e0" }}
                  onMouseEnter={e => { if (activeReport?.key !== r.key) e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)"; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}
                >
                  <p className={classes.reportTitle}>{r.title}</p>
                  <p className={classes.reportDesc}>{r.desc}</p>
                </div>
              ))}
            </CardBody>
          </Card>
        </GridItem>

        <GridItem xs={12} sm={8}>
          <Card>
            <CardHeader style={{ background: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 style={{ margin: 0, color: "#1a237e", fontWeight: "bold" }}>
                  {activeReport ? activeReport.title : "Select a Report"}
                </h4>
                {activeReport && (
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <FormControl style={{ minWidth: "180px" }}>
                      <InputLabel>Semester</InputLabel>
                      <Select value={selectedSemester} onChange={e => this.setState({ selectedSemester: e.target.value })}>
                        <MenuItem value="">— All Semesters —</MenuItem>
                        {semesters.map(s => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}{s.isCurrent ? " (Current)" : ""} — {s.academicYear?.name || ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button onClick={this.runReport} disabled={loading} color="primary">
                      {loading ? <CircularProgress size={16} style={{ color: "#fff" }} /> : "Run Report"}
                    </Button>
                    {reportData && (
                      <Button simple onClick={() => window.print()} style={{ color: "#1a237e" }}>
                        Print
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardBody>
              {!activeReport && (
                <p style={{ color: "#999", textAlign: "center", padding: "40px" }}>
                  Select a report from the list to get started.
                </p>
              )}
              {error && <p className={classes.errorMsg}>{error}</p>}
              {loading && <div style={{ textAlign: "center", padding: "30px" }}><CircularProgress /></div>}
              {!loading && reportData && this.renderTable()}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}

Reports.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(Reports));
